import { useRef, useState } from 'react';
import { TFunction } from 'i18next';

import { logError } from 'services/logging/logging';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { usePackingListStore } from 'stores/packingList/packingList';
import { useAnalytics } from 'support/helpers/analytics/analytics';

import { findInvalidNestings, InvalidNesting } from '../helpers/unitTypeHierarchy';
import { countUnitIdentifiersToGenerate, packingListToFlattenData } from '../serializer/packingListToFlattenData';
import { PackingListResult, PackingListTree } from '../types';

/** The confirmations Apply can raise, in the order they are asked. */
export type ApplyWarning = 'unplaced' | 'nesting';

const NO_ACKNOWLEDGEMENTS: Record<ApplyWarning, boolean> = { unplaced: false, nesting: false };

/**
 * Acknowledgements sit in a ref, not state: confirming one gate immediately re-enters `handleApply`
 * to evaluate the next, and a state update would not have landed by then. They reset when the user
 * backs out, so a dismissed warning is asked again rather than silently pre-approved.
 */
export const useApplyPackingList = ({
  packingList,
  quantityFieldPath,
  existingUnitIdentifiers,
  generateUnitIdentifiers,
  onApply,
  unplacedCount,
  t,
}: {
  packingList: PackingListTree;
  quantityFieldPath: string;
  existingUnitIdentifiers: Array<string>;
  generateUnitIdentifiers: (count: number) => Promise<Array<string>>;
  onApply: (result: PackingListResult) => Promise<void>;
  unplacedCount: number;
  t: TFunction;
}) => {
  const [isApplying, setIsApplying] = useState(false);
  const [activeWarning, setActiveWarning] = useState<ApplyWarning | null>(null);
  const acknowledged = useRef<Record<ApplyWarning, boolean>>({ ...NO_ACKNOWLEDGEMENTS });
  const { track } = useAnalytics();

  const invalidNestings: Array<InvalidNesting> = findInvalidNestings(packingList.transportUnits);

  const handleApply = async () => {
    if (unplacedCount > 0 && !acknowledged.current.unplaced) {
      setActiveWarning('unplaced');
      return;
    }
    if (invalidNestings.length > 0 && !acknowledged.current.nesting) {
      setActiveWarning('nesting');
      return;
    }
    setActiveWarning(null);
    setIsApplying(true);
    try {
      // Reuse runs first on the client; only the units it can't cover need fresh backend identifiers.
      const neededCount = countUnitIdentifiersToGenerate(packingList.transportUnits, existingUnitIdentifiers);
      const generated = await generateUnitIdentifiers(neededCount);
      await onApply(packingListToFlattenData(packingList, quantityFieldPath, existingUnitIdentifiers, generated));
      track('webedi:packing_list_apply.successful', {
        documentType: usePackingListStore.getState().documentType,
      });
    } catch (error) {
      // Block the apply and keep the modal open: real SSCC numbers matter, so we never fall back to
      // a fabricated identifier.
      logError(error);
      addNotification(t('webedi:packing_list.generate_error'), NotificationType.error);
    } finally {
      setIsApplying(false);
      acknowledged.current = { ...NO_ACKNOWLEDGEMENTS };
    }
  };

  const confirmWarning = () => {
    if (activeWarning) {
      acknowledged.current[activeWarning] = true;
    }
    setActiveWarning(null);
    void handleApply();
  };

  const dismissWarning = () => {
    setActiveWarning(null);
    acknowledged.current = { ...NO_ACKNOWLEDGEMENTS };
  };

  return { isApplying, activeWarning, invalidNestings, handleApply, confirmWarning, dismissWarning };
};
